import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useDispatch, useSelector } from "react-redux";
import { getTopCarriersWidget } from "../../redux/actions/adminPortal_dashboardAction";

// Tooltip copy — kept verbose so operators understand the metric, the formula,
// and what "good" looks like. ASA tooltip explicitly notes it's not PDD so the
// user knows the caveat.
const METRIC_INFO = {
  totalCalls: {
    title: "Total Calls (today)",
    description:
      "All call attempts routed through this carrier today, excluding suspended-DID hits. Answered + missed + failed combined.",
  },
  asr: {
    title: "ASR (Answer-Seizure Ratio)",
    description:
      "Percentage of today's call attempts that were actually answered (had talk time). Formula: answered_calls / total_calls × 100. Low ASR points to bad routing, dead numbers, or a carrier delivery issue.",
    thresholds: "Green: >70% · Yellow: 50-70% · Red: <50%",
  },
  acd: {
    title: "ACD (Average Call Duration)",
    description:
      "Mean talk-time across answered calls today, in seconds. Short ACD with low ASR usually signals call-quality or routing problems — callers connect but hang up fast.",
    thresholds: "Green: >180s · Yellow: 120-180s · Red: <120s",
  },
  asa: {
    title: "ASA (Average Speed of Answer)",
    description:
      "Mean seconds between dial (start_at) and connect (answer_at) across answered calls today. Lower is better — long ASA indicates slow carrier routing or buyers picking up late. Note: this is ASA, not PDD — true Post-Dial-Delay needs SIP 18x progress timing which isn't captured today.",
    thresholds: "Green: <8s · Yellow: 8-15s · Red: >15s",
  },
};

// Per-tab descriptions — these power the info icon next to each tab's title
// so the operator can see exactly which "carrier" they are looking at, where
// the data comes from, and any caveats. The word "carrier" is overloaded
// across three concepts in this product, so the wording here is deliberately
// explicit.
const SOURCE_INFO = {
  did_carrier: {
    label: "DID Carrier",
    title: "DID Carrier (Supplier / Vendor)",
    summary:
      "Ranks the DID supplier — the vendor we bought the inbound number from. Operators pick this label from a managed dropdown when adding/editing a DID.",
    dataSource:
      "rc_redirect_did.carrier_name (managed via the rc_did_carrier lookup table)",
    joinedVia: "cdr.did_number = rc_redirect_did.didnumber",
    answers:
      "Which DID supplier is bringing in the most traffic today, and how good is the quality of that traffic?",
    caveats:
      "Attribution uses the DID's CURRENT carrier mapping — if a DID is reassigned to a different supplier later, past calls re-attribute. DIDs without a configured supplier ('Unknown') are excluded from the top-4 ranking.",
  },
  termination: {
    label: "Termination",
    title: "Termination Carrier (Outbound SIP Trunk)",
    summary:
      "Ranks the outbound SIP trunk that actually carried the call out to the buyer — i.e. which termination provider delivered the leg.",
    dataSource:
      "PJSIP endpoint id parsed from cdr.dialstring (format set by re_fastagi: 'PJSIP/<number>@<endpoint_id>,<timeout>'). Maps to ps_endpoints managed by the Carrier resource.",
    joinedVia:
      "substring(cdr.dialstring from '@([^,]+)') — pulls the @endpoint segment from the dial string written at call time",
    answers:
      "Which termination carrier is carrying the most traffic today, and how is its delivery quality (ASR/ACD/ASA)?",
    caveats:
      "Multi-buyer calls only retain the LAST attempt's dialstring on the cdr row — per-attempt history lives in buyer_dial_log and is not reflected here. Calls with no dialstring (e.g. blocked before dial) collapse to 'Unknown' and are excluded from the top-4 ranking.",
  },
  caller_network: {
    label: "Caller Network",
    title: "Caller Mobile Network (Inbound Line Type)",
    summary:
      "Ranks the inbound CALLER's mobile carrier — Verizon, T-Mobile, AT&T, etc. This is per-caller-number metadata, NOT a SIP trunk.",
    dataSource:
      "cdr.carrier — populated by re_fastagi from the phone-validator line-type lookup (PhoneCompany field). Cached per-number in phone_lookup_cache.",
    joinedVia:
      "Direct GROUP BY cdr.carrier — no join needed; the value is written onto the CDR row at call setup.",
    answers:
      "Which mobile carriers are originating most of the calls landing on our DIDs today? Useful for spotting traffic patterns or fraud concentration.",
    caveats:
      "Reflects the CALLER's carrier (origination side), not anything about routing or termination. Calls where line-type lookup failed or returned blank collapse to 'Unknown' and are excluded from the top-4 ranking.",
  },
};

const TAB_ORDER = ["did_carrier", "termination", "caller_network"];

const getAsrColor = (asr) => {
  if (asr > 70) return "#2e7d32";
  if (asr >= 50) return "#f59e0b";
  return "#dc2626";
};
const getAcdColor = (acd) => {
  if (acd > 180) return "#2e7d32";
  if (acd >= 120) return "#f59e0b";
  return "#dc2626";
};
const getAsaColor = (asa, hasAnswers) => {
  if (!hasAnswers) return "#9e9e9e";
  if (asa < 8) return "#2e7d32";
  if (asa <= 15) return "#f59e0b";
  return "#dc2626";
};

const RANK_COLORS = ["#0a140e", "#0a140e", "#0a140e", "#0a140e"];

// Compact info icon next to a metric label. Hover shows title + description
// + threshold band. Mirrors the InfoIcon already used in CarrierMetricsChart.
function MetricInfoIcon({ metricKey }) {
  const info = METRIC_INFO[metricKey];
  if (!info) return null;
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
            {info.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: "#e0e0e0" }}>
            {info.description}
          </Typography>
          {info.thresholds && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, display: "block", color: "#b0bec5" }}
            >
              {info.thresholds}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <InfoOutlinedIcon sx={{ fontSize: 13, color: "#9e9e9e", cursor: "help", ml: 0.3 }} />
    </Tooltip>
  );
}

// Detailed info icon for a tab — explains what THIS particular "carrier" is,
// where its data comes from, what question it answers, and any caveats. Lets
// the operator A/B between the three views without confusion.
function SourceInfoIcon({ source, size = 16 }) {
  const info = SOURCE_INFO[source];
  if (!info) return null;
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.75, maxWidth: 360 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
            {info.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.6, color: "#e0e0e0" }}>
            {info.summary}
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.8, display: "block", color: "#90caf9" }}>
            DATA SOURCE
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#e0e0e0", whiteSpace: "pre-wrap" }}>
            {info.dataSource}
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.6, display: "block", color: "#90caf9" }}>
            HOW IT'S JOINED
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#e0e0e0", whiteSpace: "pre-wrap" }}>
            {info.joinedVia}
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.6, display: "block", color: "#90caf9" }}>
            ANSWERS
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#e0e0e0" }}>
            {info.answers}
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.6, display: "block", color: "#ffb74d" }}>
            CAVEATS
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#e0e0e0" }}>
            {info.caveats}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <InfoOutlinedIcon sx={{ fontSize: size, color: "#9e9e9e", cursor: "help", ml: 0.4 }} />
    </Tooltip>
  );
}

// One pill = one metric for one carrier. Color encodes good/bad per the
// thresholds above; tooltip explains the formula and bands.
function MetricPill({ label, value, color, metricKey }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1.1,
        py: 0.4,
        borderRadius: "10px",
        background: `${color}14`,
        border: `1px solid ${color}33`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 700,
          color: `${color} !important`,
        }}
      >
        {value}
      </Typography>
      <MetricInfoIcon metricKey={metricKey} />
    </Box>
  );
}

function TopCarriersWidget() {
  const dispatch = useDispatch();
  const [activeSource, setActiveSource] = useState("did_carrier");

  // topCarriersWidget slice is keyed per source so tab switches don't refetch
  // unnecessarily. CarrierMetricsChart still uses the legacy carrierMetrics
  // slice with the default did_carrier source server-side — that path is
  // untouched.
  const widgetState = useSelector((state) => state.topCarriersWidget);
  const loading = widgetState?.loading?.[activeSource];
  const data = widgetState?.data?.[activeSource];
  const cached = data && data.data;

  // Lazy-load the active tab's data on first activation. Each source has its
  // own cache slot so toggling tabs is free after the first load.
  useEffect(() => {
    if (!cached && !loading) {
      dispatch(getTopCarriersWidget(activeSource));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSource]);

  const top4 = useMemo(() => {
    if (!data?.data) return [];
    const today = new Date().toISOString().split("T")[0];
    return data.data
      .filter(
        (d) =>
          d.date === today &&
          d.carrier &&
          d.carrier !== "Unknown" &&
          d.total_calls > 0
      )
      .sort((a, b) => b.total_calls - a.total_calls)
      .slice(0, 4);
  }, [data]);

  return (
    <Box
      sx={{
        // Stretch to fill the parent flex slot in AdminDashboard so the
        // widget aligns vertically with ServerStatsCombined regardless of
        // how many carrier rows are rendered.
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Header chip + tabs row — chip mirrors the Server Monitoring header
          for vertical symmetry; tabs let the admin compare the three carrier
          views live without leaving the dashboard. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Chip
          icon={<LeaderboardIcon style={{ color: "#fff" }} />}
          label="Top 4 Carriers · Today"
          sx={{
            px: 1.2,
            py: 1,
            fontWeight: 600,
            background:
              "linear-gradient(135deg, #0a140e 0%, #0a140e 100%)",
            color: "#fff",
            fontSize: "0.85rem",
            letterSpacing: 0.5,
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />
        <Tooltip
          title={
            <Box sx={{ p: 0.5, maxWidth: 320 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
                What "carrier" means here
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: "#e0e0e0" }}>
                The word "carrier" is overloaded across three concepts in this
                product. Use the tabs to switch views — DID Carrier (the DID
                supplier), Termination (the outbound SIP trunk), and Caller
                Network (the inbound caller's mobile network). The info icon
                on each tab explains the data source and caveats in detail.
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9e9e9e", cursor: "help" }} />
        </Tooltip>
      </Box>

      <Tabs
        value={activeSource}
        onChange={(_e, v) => setActiveSource(v)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          mb: 1,
          "& .MuiTab-root": {
            minHeight: 36,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.78rem",
            color: "#475569",
          },
          "& .Mui-selected": { color: "#0a140e !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#0a140e" },
        }}
      >
        {TAB_ORDER.map((src) => (
          <Tab
            key={src}
            value={src}
            label={
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                {SOURCE_INFO[src].label}
                <SourceInfoIcon source={src} size={14} />
              </Box>
            }
          />
        ))}
      </Tabs>

      <Card
        sx={{
          p: 1.5,
          borderRadius: "20px",
          background: "#ffffffcc",
          backdropFilter: "blur(10px)",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
          minHeight: 340,
          flex: 1,
        }}
      >
        {loading && (
          <Stack spacing={0.8}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={56}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        )}

        {!loading && top4.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <LeaderboardIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#212121 !important" }}>
              No {SOURCE_INFO[activeSource].label.toLowerCase()} traffic today
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>
              Cards populate once calls land that match this view's data source.
            </Typography>
          </Box>
        )}

        {!loading && top4.length > 0 && (
          <Stack spacing={0.8}>
            {top4.map((c, idx) => {
              const asrColor = getAsrColor(c.asr);
              const acdColor = getAcdColor(c.acd);
              const asaColor = getAsaColor(c.asa, c.answered_calls > 0);
              const rankColor = RANK_COLORS[idx] || "#0a140e";
              return (
                <Box
                  key={`${c.carrier}-${idx}`}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    transition: "0.2s",
                    "&:hover": { background: "#f1f5f9", transform: "translateY(-1px)" },
                  }}
                >
                  {/* Header: rank + carrier name + total calls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: rankColor,
                        color: "#fff !important",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "#0a140e !important",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={c.carrier}
                    >
                      {c.carrier}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 800,
                          color: "#1976d2 !important",
                          lineHeight: 1,
                        }}
                      >
                        {c.total_calls}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#64748b !important",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        calls
                      </Typography>
                      <MetricInfoIcon metricKey="totalCalls" />
                    </Box>
                  </Box>

                  {/* Metric pills */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                    <MetricPill
                      label="ASR"
                      value={`${c.asr}%`}
                      color={asrColor}
                      metricKey="asr"
                    />
                    <MetricPill
                      label="ACD"
                      value={`${c.acd}s`}
                      color={acdColor}
                      metricKey="acd"
                    />
                    <MetricPill
                      label="ASA"
                      value={`${c.asa}s`}
                      color={asaColor}
                      metricKey="asa"
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Card>
    </Box>
  );
}

export default TopCarriersWidget;
